import { useState } from 'react';

type ListingType = 'template' | 'plugin' | 'theme' | 'tutorial';
type ListingStatus = 'published' | 'draft' | 'under_review';

interface CreatorListing {
  id: string;
  type: ListingType;
  title: string;
  emoji: string;
  price: number;
  sales: number;
  revenue: number;
  rating: number;
  status: ListingStatus;
  createdAt: string;
}

const MOCK_LISTINGS: CreatorListing[] = [
  { id: 'cl1', type: 'template', title: 'HVAC Scheduler Starter', emoji: '🔧', price: 29.99, sales: 47, revenue: 1197.78, rating: 4.8, status: 'published', createdAt: '2 weeks ago' },
  { id: 'cl2', type: 'plugin', title: 'Twilio Voice Integration', emoji: '📞', price: 9.99, sales: 123, revenue: 1044.33, rating: 4.5, status: 'published', createdAt: '1 month ago' },
  { id: 'cl3', type: 'theme', title: 'Neon Cyberpunk Theme', emoji: '🌃', price: 4.99, sales: 89, revenue: 377.10, rating: 4.9, status: 'published', createdAt: '3 weeks ago' },
  { id: 'cl4', type: 'tutorial', title: 'Agent Optimization Masterclass', emoji: '📖', price: 14.99, sales: 31, revenue: 394.67, rating: 4.7, status: 'published', createdAt: '1 week ago' },
  { id: 'cl5', type: 'template', title: 'Multi-Agent Pipeline Kit', emoji: '🔀', price: 49.99, sales: 0, revenue: 0, rating: 0, status: 'draft', createdAt: 'Today' },
];

const TYPE_ICONS: Record<ListingType, string> = {
  template: '📋',
  plugin: '🔌',
  theme: '🎨',
  tutorial: '📖',
};

export function CreatorToolsPage() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'create'>('dashboard');
  const [listingType, setListingType] = useState<ListingType>('template');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [published, setPublished] = useState(false);

  const totalRevenue = MOCK_LISTINGS.reduce((sum, l) => sum + l.revenue, 0);
  const totalSales = MOCK_LISTINGS.reduce((sum, l) => sum + l.sales, 0);
  const SYMSVOXCut = totalRevenue * 0.15;
  const creatorEarnings = totalRevenue - SYMSVOXCut;

  const handlePublish = () => {
    if (!title.trim() || !price.trim()) return;
    setPublished(true);
    setTimeout(() => {
      setPublished(false);
      setTitle('');
      setDescription('');
      setPrice('');
      setActiveTab('dashboard');
    }, 2000);
  };

  return (
    <div className="space-y-3">
      {/* Header + Tabs */}
      <div className="social-box overflow-hidden">
        <div className="box-header flex items-center justify-between">
          <span>Creator Tools</span>
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('dashboard')}
              className="px-2 py-0.5 text-2xs font-bold cursor-pointer"
              style={{
                background: activeTab === 'dashboard' ? 'var(--accent)' : 'var(--bg-filter)',
                color: activeTab === 'dashboard' ? 'white' : 'var(--text-muted)',
                border: '1px solid var(--border-separator)',
              }}
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('create')}
              className="px-2 py-0.5 text-2xs font-bold cursor-pointer"
              style={{
                background: activeTab === 'create' ? 'var(--accent)' : 'var(--bg-filter)',
                color: activeTab === 'create' ? 'white' : 'var(--text-muted)',
                border: '1px solid var(--border-separator)',
              }}
            >
              + Create Listing
            </button>
          </div>
        </div>

        {/* Revenue Stats */}
        <div className="flex gap-4 px-3 py-2" style={{ borderBottom: '1px solid var(--border-separator)', background: 'var(--bg-deep)' }}>
          <div className="text-center flex-1">
            <div className="font-bold text-sm" style={{ color: '#10b981' }}>${creatorEarnings.toFixed(2)}</div>
            <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Your Earnings (85%)</div>
          </div>
          <div className="text-center flex-1">
            <div className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>${totalRevenue.toFixed(2)}</div>
            <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Total Revenue</div>
          </div>
          <div className="text-center flex-1">
            <div className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{totalSales}</div>
            <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Total Sales</div>
          </div>
          <div className="text-center flex-1">
            <div className="font-bold text-sm" style={{ color: 'var(--text-muted)' }}>${SYMSVOXCut.toFixed(2)}</div>
            <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Platform Fee (15%)</div>
          </div>
        </div>
      </div>

      {activeTab === 'dashboard' ? (
        <>
          {/* Listings */}
          <div className="social-box overflow-hidden">
            <div className="box-header">Your Listings</div>
            {MOCK_LISTINGS.map((listing, i) => (
              <div
                key={listing.id}
                className="flex items-center gap-3 px-3 py-2.5"
                style={{ borderBottom: i < MOCK_LISTINGS.length - 1 ? '1px solid var(--border-separator)' : 'none' }}
              >
                <div
                  className="shrink-0 w-10 h-10 flex items-center justify-center text-lg"
                  style={{ background: 'var(--bg-filter)', border: '1px solid var(--border-separator)' }}
                >
                  {listing.emoji}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xs" style={{ color: 'var(--text-primary)' }}>{listing.title}</span>
                    <span className="text-2xs" style={{ color: 'var(--text-muted)' }}>{TYPE_ICONS[listing.type]} {listing.type}</span>
                    <span
                      className="px-1 py-0.5 text-2xs font-bold"
                      style={{
                        background: listing.status === 'published' ? '#10b98120' : listing.status === 'draft' ? '#6b728020' : '#f59e0b20',
                        color: listing.status === 'published' ? '#10b981' : listing.status === 'draft' ? '#6b7280' : '#f59e0b',
                      }}
                    >
                      {listing.status}
                    </span>
                  </div>
                  <div className="flex gap-3 mt-0.5 text-2xs" style={{ color: 'var(--text-muted)' }}>
                    <span>${listing.price}</span>
                    <span>{listing.sales} sales</span>
                    <span>${listing.revenue.toFixed(2)} revenue</span>
                    {listing.rating > 0 && <span>{'⭐'.repeat(Math.round(listing.rating))} {listing.rating}</span>}
                    <span>{listing.createdAt}</span>
                  </div>
                </div>

                <div className="flex gap-1 shrink-0">
                  <button className="px-2 py-0.5 text-2xs cursor-pointer" style={{ background: 'var(--bg-filter)', color: 'var(--text-muted)', border: '1px solid var(--border-separator)' }}>Edit</button>
                  <button className="px-2 py-0.5 text-2xs cursor-pointer" style={{ background: 'var(--bg-filter)', color: 'var(--text-muted)', border: '1px solid var(--border-separator)' }}>Stats</button>
                </div>
              </div>
            ))}
          </div>

          {/* Revenue Breakdown */}
          <div className="social-box overflow-hidden">
            <div className="box-header">Revenue by Type</div>
            <div className="p-3 space-y-2">
              {(['template', 'plugin', 'theme', 'tutorial'] as ListingType[]).map(type => {
                const typeListings = MOCK_LISTINGS.filter(l => l.type === type);
                const typeRevenue = typeListings.reduce((sum, l) => sum + l.revenue, 0);
                const pct = totalRevenue > 0 ? (typeRevenue / totalRevenue) * 100 : 0;

                return (
                  <div key={type}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>
                        {TYPE_ICONS[type]} {type.charAt(0).toUpperCase() + type.slice(1)}s
                      </span>
                      <span className="text-2xs" style={{ color: 'var(--text-muted)' }}>${typeRevenue.toFixed(2)} ({Math.round(pct)}%)</span>
                    </div>
                    <div className="w-full h-2" style={{ background: 'var(--bg-filter)', border: '1px solid var(--border-separator)' }}>
                      <div className="h-full" style={{ width: `${pct}%`, background: 'var(--accent)' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        /* Create New Listing */
        <div className="social-box overflow-hidden">
          <div className="box-header">Create New Listing</div>
          <div className="p-3 space-y-3">
            {published && (
              <div className="p-2 text-center" style={{ background: '#10b98120', border: '1px solid #10b98140' }}>
                <span className="font-bold text-xs" style={{ color: '#10b981' }}>Listing submitted for review!</span>
              </div>
            )}

            {/* Type Selection */}
            <div>
              <label className="text-2xs font-bold block mb-1" style={{ color: 'var(--text-muted)' }}>TYPE</label>
              <div className="flex gap-1">
                {(['template', 'plugin', 'theme', 'tutorial'] as ListingType[]).map(type => (
                  <button
                    key={type}
                    onClick={() => setListingType(type)}
                    className="flex-1 py-1.5 text-xs font-bold cursor-pointer"
                    style={{
                      background: listingType === type ? 'var(--accent)' : 'var(--bg-filter)',
                      color: listingType === type ? 'white' : 'var(--text-muted)',
                      border: '1px solid var(--border-separator)',
                    }}
                  >
                    {TYPE_ICONS[type]} {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="text-2xs font-bold block mb-1" style={{ color: 'var(--text-muted)' }}>TITLE</label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="My Awesome Template"
                className="w-full px-2 py-1.5 text-xs"
                style={{ background: 'var(--bg-filter)', border: '1px solid var(--border-separator)', color: 'var(--text-primary)' }}
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-2xs font-bold block mb-1" style={{ color: 'var(--text-muted)' }}>DESCRIPTION</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe what your listing does..."
                rows={4}
                className="w-full px-2 py-1.5 text-xs resize-none"
                style={{ background: 'var(--bg-filter)', border: '1px solid var(--border-separator)', color: 'var(--text-primary)' }}
              />
            </div>

            {/* Price */}
            <div>
              <label className="text-2xs font-bold block mb-1" style={{ color: 'var(--text-muted)' }}>PRICE (USD)</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={price}
                  onChange={e => setPrice(e.target.value)}
                  placeholder="9.99"
                  min="0"
                  step="0.01"
                  className="w-32 px-2 py-1.5 text-xs"
                  style={{ background: 'var(--bg-filter)', border: '1px solid var(--border-separator)', color: 'var(--text-primary)' }}
                />
                <span className="text-2xs" style={{ color: 'var(--text-muted)' }}>
                  You earn: ${price ? (parseFloat(price) * 0.85).toFixed(2) : '0.00'} (85%) | Platform: ${price ? (parseFloat(price) * 0.15).toFixed(2) : '0.00'} (15%)
                </span>
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-2">
              <button
                onClick={handlePublish}
                className="px-4 py-1.5 text-xs font-bold cursor-pointer"
                style={{ background: 'var(--accent)', color: 'white', border: '1px solid var(--accent-hover)' }}
              >
                Submit for Review
              </button>
              <button
                className="px-4 py-1.5 text-xs font-bold cursor-pointer"
                style={{ background: 'var(--bg-filter)', color: 'var(--text-muted)', border: '1px solid var(--border-separator)' }}
              >
                Save as Draft
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
